import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Универсальная форма для одного раздела настроек.
 *
 * Источник правды о наборе полей — backend (`schema` в ответе /api/settings/{key}).
 * Кнопка «Сохранить» отключена, если у пользователя нет права settings.{key}:write
 * (и сами поля становятся read-only).
 *
 * Метки полей хранятся отдельно (в LABELS) — они не должны лежать в БД.
 */
const LABELS = {
  push: {
    title: 'Пуш-уведомления (мобильные клиенты)',
    description: 'Отправка пуш-уведомлений на мобильные приложения родителей и сотрудников. Android получает пуши через Firebase Cloud Messaging, iPhone — напрямую через Apple APNs (Firebase для iOS не нужен). Устройства регистрируются автоматически при входе в мобильное приложение.',
    fields: {
      enabled:                  'Отправка пушей',
      fcm_project_id:           'FCM (Android): Project ID',
      fcm_service_account_json: 'FCM (Android): сервисный аккаунт (JSON)',
      apns_team_id:             'APNs (iPhone): Team ID',
      apns_key_id:              'APNs (iPhone): Key ID',
      apns_bundle_id:           'APNs (iPhone): Bundle ID приложения',
      apns_auth_key_p8:         'APNs (iPhone): ключ .p8',
      apns_sandbox:             'APNs (iPhone): контур',
    },
    enums: {
      enabled: [
        { v: 'on',  label: 'Включена' },
        { v: 'off', label: 'Выключена' },
      ],
      apns_sandbox: [
        { v: 'on',  label: 'Sandbox (dev-сборки из Xcode)' },
        { v: 'off', label: 'Production (TestFlight / App Store)' },
      ],
    },
    hints: {
      fcm_project_id:           'Идентификатор проекта из консоли Firebase (Project settings → General).',
      fcm_service_account_json: 'Содержимое JSON-файла сервисного аккаунта: Firebase console → Project settings → Service accounts → Generate new private key. Вставьте весь JSON целиком.',
      apns_team_id:             'Apple Developer → Membership → Team ID (10 символов).',
      apns_key_id:              'Apple Developer → Certificates, IDs & Profiles → Keys: создайте ключ с галкой APNs; Key ID — 10 символов.',
      apns_bundle_id:           'Bundle Identifier iOS-приложения (например ru.npschool.crm). Уходит в заголовок apns-topic.',
      apns_auth_key_p8:         'Содержимое скачанного файла AuthKey_XXXXXXXXXX.p8 целиком (строки BEGIN/END PRIVATE KEY). Скачать его можно только один раз при создании ключа.',
      apns_sandbox:             'Dev-сборка из Xcode получает sandbox-токены, TestFlight и App Store — боевые. Если пуши не доходят на тестовую сборку — переключите контур.',
    },
  },
  api_log: {
    title: 'Лог API-запросов',
    description: 'Журнал всех запросов к API: кто, когда и что запросил (метод, путь, параметры без паролей и файлов), IP, браузер, код ответа и длительность. Смотреть — в «Отчёты → Лог API-запросов» (нужно отдельное право). Размер таблицы ограничен: при превышении лимита самые старые записи удаляются автоматически.',
    fields: {
      enabled:      'Вести лог',
      max_table_mb: 'Максимальный размер таблицы, МБ',
    },
    enums: {
      enabled: [
        { v: 'on',  label: 'Включён' },
        { v: 'off', label: 'Выключен' },
      ],
    },
    hints: {
      enabled:      'При выключении новые запросы не записываются; уже накопленный лог остаётся.',
      max_table_mb: 'По умолчанию 200 МБ. При превышении удаляются самые старые записи (примерно по 10% за раз). Физический файл на диске после удаления не сжимается сам — только перестаёт расти.',
    },
  },
  org: {
    title: 'Параметры организации',
    description: 'Общие данные школы: название, базовый URL CRM, контактные адреса. Эти значения автоматически подставляются в шаблоны писем как «глобальные переменные» — {{school_name}}, {{crm_base_url}} и т.п. То же используется в печатных формах.',
    fields: {
      school_name:        'Краткое название школы',
      school_full_name:   'Полное название (для договоров)',
      site_title:         'Заголовок вкладки браузера',
      crm_base_url:       'Базовый URL CRM',
      lk_base_url:        'URL личного кабинета родителя',
      support_email:      'Контактный email',
      support_phone:      'Контактный телефон',
    },
    hints: {
      school_name:      'Например: ЧОШ «Новое поколение». Идёт в шапку и подпись писем.',
      school_full_name: 'Например: АНО ЧОШ «Новое поколение». Используется в шапке договоров и официальных уведомлений.',
      site_title:       'То, что отображается в названии вкладки браузера и закладках. Например: «Школьная CRM — Новое поколение». Если пусто, используется значение по умолчанию.',
      crm_base_url:     'Без слеша в конце. Например: https://crm2.np-school.ru. Используется при сборке ссылок в письмах (восстановление пароля).',
      lk_base_url:      'Например: https://crm2.np-school.ru/lk. По этому URL переходят родители из писем при первом входе.',
      support_email:    'Адрес, на который родители могут написать; добавляется в подвал писем.',
      support_phone:    'Телефон школы. Добавляется в подвал писем и в подписи договоров.',
    },
  },
  mail: {
    title: 'Настройка почты (SMTP)',
    description: 'SMTP-сервер для исходящих писем — приглашения, уведомления, сброс пароля. Все письма проходят через очередь email_outbox: PHP кладёт в БД, Node-воркер шлёт через указанный здесь сервер.',
    fields: {
      smtp_host:            'SMTP-сервер',
      smtp_port:            'Порт',
      smtp_secure:          'Шифрование',
      smtp_user:             'Логин',
      smtp_password:        'Пароль',
      smtp_skip_tls_verify: 'Не проверять TLS-сертификат',
      sender_email:         'Email отправителя',
      sender_name:          'Имя отправителя',
    },
    enums: {
      smtp_secure: [
        { v: 'none', label: 'Без шифрования' },
        { v: 'tls',  label: 'STARTTLS' },
        { v: 'ssl',  label: 'SSL/TLS' },
      ],
      smtp_skip_tls_verify: [
        { v: 'off', label: 'Проверять (рекомендуется)' },
        { v: 'on',  label: 'Не проверять' },
      ],
    },
    hints: {
      smtp_skip_tls_verify: 'Включайте только при самоподписанном сертификате внутреннего SMTP. Снижает безопасность — возможен MITM. В проде держите выключенным.',
    },
    warnings: ['smtp_skip_tls_verify'],
  },
  '1c': {
    title: 'Настройка 1С',
    description: 'Подключение к базе 1С. Кэш контрагентов и договоров обновляет node-сервис, периодически опрашивая GET {Адрес базы}/hs/crm_api/students/.',
    fields: {
      base_url:              'Адрес базы',
      login:                 'Логин',
      password:              'Пароль',
      sync_interval_seconds: 'Интервал синхронизации, сек.',
    },
  },
  '1c_zup': {
    title: 'Настройка 1С ЗУП (зарплата)',
    description: 'Отдельная база 1С с кадрами и зарплатой. Используется для выгрузки начислений учителям за доп. занятия — создаёт документы «Разовое начисление» по предметам (часы × ставка).',
    fields: {
      base_url:              'Адрес базы',
      login:                 'Логин',
      password:              'Пароль',
      teacher_tax_rate:      'Ставка НДФЛ (доля)',
    },
    hints: {
      teacher_tax_rate:      'Например 0.13 для 13%. Используется чтобы пересчитать «договорную» сумму за занятие в почасовую: ставка = цена / (1 − НДФЛ).',
    },
  },
  modulkassa: {
    title: 'Настройка Модулькассы',
    description: 'Касса в офисе принимает оплату наличными или картой. CRM создаёт заказ на устройстве, кассир пробивает его, и касса присылает webhook с типом оплаты (CARD/CASH). Webhook должен быть зарегистрирован в кассе как responseURL.',
    fields: {
      retail_point_id:      'UUID кассы (retail point)',
      api_user:             'API · логин',
      api_password:         'API · пароль',
      webhook_url_template: 'URL webhook (после оплаты)',
      fn_user:              'ОФД · логин (опционально)',
      fn_password:          'ОФД · пароль (опционально)',
    },
    hints: {
      webhook_url_template: 'Абсолютный URL. Подставляется {paymentId}. Например: https://crm2.np-school.ru/api/webhooks/modulkassa?paymentId={paymentId}',
      fn_user: 'Нужно только если планируете самостоятельно отправлять чеки в ОФД (отдельный сервис api/fn). Для базового сценария «оплата на кассе» — оставить пустым.',
    },
  },
  yookassa: {
    title: 'Настройка ЮКассы',
    description: 'Две связки credentials — рабочая и тестовая. Активная выбирается полем «Режим». Webhook-приёмник один на магазин: https://{ваш-домен}/api/webhooks/yookassa — зарегистрируйте его в личном кабинете ЮКассы для событий payment.succeeded / payment.canceled / payment.waiting_for_capture / refund.succeeded.',
    fields: {
      mode:                'Режим',
      live_shop_id:        'Live · shopId',
      live_secret_key:     'Live · secretKey',
      test_shop_id:        'Test · shopId',
      test_secret_key:     'Test · secretKey',
      return_url_template: 'URL возврата (после оплаты)',
    },
    enums: {
      mode: [
        { v: 'test', label: 'Тест (sandbox)' },
        { v: 'live', label: 'Боевой' },
      ],
    },
    hints: {
      return_url_template: 'Абсолютный URL. Подставляется {paymentId} — наш ID платежа. Например: https://crm2.np-school.ru/payments?open={paymentId} — пользователь вернётся в список платежей с открытой карточкой; статус обновится через WS, когда придёт webhook.',
    },
  },
  receipt: {
    title: 'Чеки (ОФД)',
    description: 'Шаблоны позиций фискального чека. У нас в платеже хранится «нормальный» список (тариф/каждое занятие отдельно), но в чек строки сворачиваются: основные услуги — каждая своей позицией, а ВСЕ доп. занятия — одной общей строкой «Дополнительные образовательные услуги».',
    fields: {
      label_school_admission_fee: 'Вступительный взнос — школа',
      label_school_monthly:       'Ежемесячная оплата — школа',
      label_kg_admission_fee:     'Вступительный взнос — детский сад',
      label_kg_monthly:           'Ежемесячная оплата — детский сад',
      label_extras_combined:      'Объединённая позиция доп. занятий',
      label_manual:               '«Прочие услуги» (для manual-позиций)',
      vat_rate:                   'Ставка НДС в чеке, %',
    },
    hints: {
      label_extras_combined: 'Все позиции с kind=extra_lesson и extra_camp в чеке сольются в одну строку с этим названием.',
      vat_rate: '0 = без НДС (для НКО). 5 / 7 / 10 / 20 — соответствующая ставка. Уточните у вашего ОФД.',
    },
  },
  // skud — обслуживается кастомной панелью SkudSettingsPanel (см. App.jsx).
  // Этот блок — fallback на случай, если по какой-то причине отрабатывает
  // SettingsForm (например, пока не пересобрался фронт после создания
  // нового файла панели). Подписи должны быть человекочитаемыми.
  skud: {
    title: 'Настройка СКУД (Orion)',
    description: 'Параметры подключения к серверу системы контроля доступа: MSSQL (источник кэша и журнала событий) и JSON-RPC (управление людьми / пропусками / открытием дверей).',
    fields: {
      sync_enabled:    'Синхронизация',
      host:            'Хост',
      soap_port:       'Порт JSON-RPC',
      soap_user:       'Логин SOAP',
      soap_password:   'Пароль SOAP',
      mssql_port:      'Порт MSSQL',
      mssql_instance:  'Инстанс MSSQL',
      mssql_user:      'Логин MSSQL',
      mssql_password:  'Пароль MSSQL',
      mssql_database:  'База MSSQL',
      department_employees_skud_id: 'Подразделение «Сотрудники»',
      department_students_skud_id:  'Подразделение «Ученики»',
      department_parents_skud_id:   'Подразделение «Родители»',
    },
    enums: {
      sync_enabled: [
        { v: 'on',  label: 'Включена' },
        { v: 'off', label: 'Выключена' },
      ],
    },
    hints: {
      sync_enabled:   'Когда выключена — все таймеры синхронизации пропускают тики. Подходит для отладки и инцидентов.',
      host:           'Один общий хост для MSSQL и JSON-RPC, например orion.ng.local.',
      soap_port:      'Эндпоинт JSON-RPC: http://<хост>:<порт>/jsonrpc/iorionpro. Обычно 8090.',
      soap_user:      'Используется для getLoginToken — нужен только для команд управления (открытие точки доступа и т.п.).',
      soap_password:  'Хранится в БД как есть; в MD5 хешируется на лету при вызове Orion.',
      mssql_instance: 'Для дефолтного инстанса — MSSQLSERVER. Для именованных — например, SQLEXPRESS.',
      department_employees_skud_id: 'skud_id подразделения СКУД для учителей и администраторов. Появится в селекте после первой успешной синхронизации.',
      department_students_skud_id:  'skud_id подразделения СКУД для школьников и детсадовцев.',
      department_parents_skud_id:   'skud_id подразделения СКУД для родителей.',
    },
  },
  ws: {
    title: 'WebSocket',
    description: 'Локальный порт node-сервиса. nginx проксирует /ws → 127.0.0.1:порт. После изменения порта требуется перезапустить node-сервис.',
    fields: {
      port: 'Порт',
    },
  },
  ad: {
    title: 'Active Directory',
    description: 'Подключение к контроллеру домена. Используется для входа сотрудников по AD-логину (Этап 3) и автоматического создания учётных записей AD из CRM (Этап 4). Сервисная учётка нужна только для создания/групп — для самого входа достаточно host/port/encryption/base_dn.',
    fields: {
      enabled:               'Вход через AD',
      host:                  'Хост контроллера домена',
      port:                  'Порт',
      encryption:            'Шифрование',
      tls_skip_verify:       'Не проверять сертификат',
      base_dn:               'Base DN',
      user_search_ou:        'OU для поиска пользователей',
      service_user_dn:       'Сервисная учётка (DN)',
      service_user_password: 'Пароль сервисной учётки',
      new_user_ou:           'OU для новых пользователей',
      default_groups:        'Группы по умолчанию (DN, по одной в строке)',
      default_password:      'Пароль по умолчанию',
    },
    enums: {
      enabled: [
        { v: 'on',  label: 'Включён' },
        { v: 'off', label: 'Выключен' },
      ],
      encryption: [
        { v: 'ldaps',    label: 'LDAPS (порт 636)' },
        { v: 'starttls', label: 'STARTTLS (порт 389)' },
        { v: 'none',     label: 'Без шифрования (только для тестов)' },
      ],
      tls_skip_verify: [
        { v: 'off', label: 'Проверять (рекомендуется)' },
        { v: 'on',  label: 'Не проверять' },
      ],
    },
    hints: {
      tls_skip_verify: 'Включайте только если AD использует самоподписанный сертификат, и его CA нельзя добавить в доверенные на сервере. Включение делает соединение уязвимым для MITM-атак.',
      user_search_ou: 'В пикере «Пользователь AD» при выборе сотрудника поиск идёт только внутри этой OU. Если пусто — используется Base DN (весь домен).',
      default_password: 'Используется при создании новых пользователей в AD из CRM. Сотрудник обязан сменить его при первом входе. Пароль должен соответствовать политике сложности AD.',
    },
    warnings: ['tls_skip_verify'],
    multiline: ['default_groups'],
  },
  telephony: {
    title: 'Телефония (Asterisk/FreePBX CDR)',
    description: 'Подключение к CDR-БД Asterisk для журнала звонков и каталог записей. Используется отдельная read-only учётка MySQL и (опц.) bearer-токен для скачивания файлов записей.',
    fields: {
      enabled:              'Интеграция с телефонией',
      db_host:              'Хост MySQL (CDR)',
      db_port:              'Порт MySQL',
      db_name:              'База CDR',
      db_user:              'Логин MySQL (read-only)',
      db_password:          'Пароль MySQL',
      db_table:             'Имя таблицы CDR',
      recordings_base_url:  'Базовый URL каталога записей',
      recordings_token:     'Bearer-токен доступа к записям',
      recordings_skip_tls:  'Не проверять TLS на записях',
      recordings_path_tpl:  'Шаблон относительного пути к файлу',
      ami_host:             'AMI: хост',
      ami_port:             'AMI: порт',
      ami_user:             'AMI: пользователь',
      ami_secret:           'AMI: секрет',
      ami_channel_tpl:      'AMI: шаблон канала',
      ami_context:          'AMI: контекст диалплана',
      ami_caller_id:        'AMI: CallerID для канала',
      ami_number_prefix:    'Префикс для выхода',
      ami_plus7_to_8:       'Преобразовывать +7… в 8…',
      ami_intl_prefix:      'Международный префикс',
    },
    enums: {
      enabled: [
        { v: 'on',  label: 'Включена' },
        { v: 'off', label: 'Выключена' },
      ],
      recordings_skip_tls: [
        { v: 'off', label: 'Проверять (рекомендуется)' },
        { v: 'on',  label: 'Не проверять' },
      ],
      ami_plus7_to_8: [
        { v: 'on',  label: 'Да: +7XXX → 8XXX (РФ)' },
        { v: 'off', label: 'Нет: оставлять как есть (без +)' },
      ],
    },
    hints: {
      db_name:              'Для FreePBX обычно «asteriskcdrdb». Проверить можно командой `asterisk -rx "cdr show status"` на самом PBX.',
      db_user:              'Создайте отдельную учётку только с SELECT на таблицу cdr. Что-то вроде CREATE USER \'cdr_ro\'@\'%\' IDENTIFIED BY \'…\'; GRANT SELECT ON asteriskcdrdb.cdr TO \'cdr_ro\'@\'%\';',
      db_table:             'Обычно «cdr». В кастомных установках бывают префиксы.',
      recordings_base_url:  'Например, https://pbx.np.local/recordings — путь, по которому PBX отдаёт файлы из /var/spool/asterisk/monitor. Проще всего поднять nginx-location с alias и basic-auth/bearer.',
      recordings_token:     'Если записи защищены Bearer-токеном — указываем здесь. Будет передаваться в Authorization при стриминге файла CRM-у.',
      recordings_skip_tls:  'Включайте только при самоподписанном сертификате PBX, который нельзя положить в доверенные.',
      recordings_path_tpl:  'FreePBX 13+ кладёт записи в Y/m/d/. Шаблон по умолчанию: «{Y}/{m}/{d}/{filename}». Если у вас плоский каталог — поставьте «{filename}».',
      ami_host:             'IP или DNS Asterisk-сервера, доступный с этого CRM. Обычно тот же, что и в db_host.',
      ami_port:             'По умолчанию 5038. Должен быть открыт в файрволе PBX для IP этого CRM.',
      ami_user:             'Имя пользователя из секции [user] в /etc/asterisk/manager.conf (или из FreePBX → Asterisk Manager Users).',
      ami_secret:           'Поле secret из manager.conf. Тот самый «ключ».',
      ami_channel_tpl:      'Куда дозваниваться сотруднику. По умолчанию «PJSIP/{ext}» (FreePBX 13+). Для классического chan_sip — «SIP/{ext}». «{ext}» подставится из ext_phone сотрудника.',
      ami_context:          'Контекст исходящих звонков. В FreePBX — «from-internal». В кастомных — свой. Если ошибка «Extension does not exist» — обычно ловится диалплан другого контекста.',
      ami_caller_id:        'Можно оставить пустым — Asterisk проставит свой. Полезно, если нужно подменить вход. Поддерживает формат «\"Имя\" <номер>».',
      ami_number_prefix:    'Если в диалплане выход на транк через цифру (например, «9»), укажите её здесь — приставится в начало.',
      ami_plus7_to_8:       'Если в БД телефоны как +7XXXXXXXXXX, а диалплан ждёт 8XXXXXXXXXX — включите. После преобразования номера всегда уходят без «+».',
      ami_intl_prefix:      'Для не-российских номеров (+1, +49, …) подставится вместо «+». Стандартный «выход за рубеж из РФ» — «810», то есть «+14155551234» → «81014155551234». Если пусто — «+» просто срезается.',
    },
    warnings: ['recordings_skip_tls'],
  },
  maps: {
    title: 'Карты (Яндекс.Геокодер)',
    description: 'Node-воркер раз в полчаса берёт пачку контактов, у которых ещё нет координат, и проставляет их через Геокодер Яндекс.Карт. После изменения адреса в карточке контакта координаты автоматически сбрасываются и воркер геокодирует заново.',
    fields: {
      geocode_enabled:          'Геокодирование',
      yandex_api_key:           'API-ключ Яндекс.Карт',
      geocode_interval_seconds: 'Интервал тика воркера, сек',
      geocode_batch_size:       'Размер пачки за тик',
      geocode_throttle_ms:      'Задержка между запросами, мс',
    },
    enums: {
      geocode_enabled: [
        { v: 'on',  label: 'Включено' },
        { v: 'off', label: 'Выключено' },
      ],
    },
    hints: {
      yandex_api_key:           'Получите в кабинете Яндекс.Карт (services.yandex.ru/maps). Без него воркер пропускает тик.',
      geocode_interval_seconds: 'По умолчанию 1800 (30 минут). Слишком частые тики не нужны — координаты ставятся один раз на адрес.',
      geocode_batch_size:       'Сколько контактов обрабатывать за один тик. По умолчанию 50 — как в старой системе.',
      geocode_throttle_ms:      'Пауза между HTTP-запросами в Яндекс. По умолчанию 300 мс, чтобы не упереться в лимиты.',
    },
  },
  exchange: {
    title: 'Exchange (почтовые ящики)',
    description: 'Создание и снятие почтовых ящиков в on-prem Exchange. CRM не вызывает Exchange Management Shell напрямую — общается с маленьким PowerShell-агентом на Windows-сервере. Скрипт агента и инструкция по развёртыванию: bin/exchange-agent/ в репозитории.',
    fields: {
      enabled:          'Интеграция с Exchange',
      agent_url:        'URL агента',
      shared_secret:    'Общий секрет (Bearer-токен)',
      default_database: 'Mailbox Database по умолчанию',
      tls_skip_verify:  'Не проверять TLS-сертификат агента',
      timeout_seconds:  'Тайм-аут запросов, сек.',
      preferred_dc:     'Контроллер домена для запросов Exchange',
    },
    enums: {
      enabled: [
        { v: 'on',  label: 'Включена' },
        { v: 'off', label: 'Выключена' },
      ],
      tls_skip_verify: [
        { v: 'off', label: 'Проверять (рекомендуется)' },
        { v: 'on',  label: 'Не проверять' },
      ],
    },
    hints: {
      agent_url:        'Например, https://exch-agent.np.local:8443. Должен быть достижим из контейнера/хоста CRM.',
      shared_secret:    'Тот же секрет передаётся агенту в параметре -SharedSecret. Используется как Bearer-токен в заголовке Authorization.',
      default_database: 'Если оставить пусто — Exchange сам выберет базу по правилам автоматического распределения.',
      tls_skip_verify:  'Включайте, только если у агента самоподписанный сертификат, который нельзя положить в доверенные.',
      timeout_seconds:  'Enable-Mailbox может выполняться 10–60 секунд, поэтому рекомендуется 60+.',
      preferred_dc:     'FQDN контроллера домена, на котором CRM создаёт пользователей через AD-настройки. Если указать, Exchange будет искать пользователя именно на нём — это снимает проблему задержки репликации сразу после создания учётки. Обычно совпадает с settings.ad.host. Если оставить пусто — Exchange выбирает DC сам.',
    },
    warnings: ['tls_skip_verify'],
  },
};

export default function SettingsForm({ section: sectionProp } = {}) {
  // Поддерживаем оба сценария:
  //   • роутом /settings/:section — section берётся из URL,
  //   • вложением в кастомную панель — section приходит пропом,
  //     например <SettingsForm section="maps" /> в MapsSettingsPanel.
  const { section: sectionUrl } = useParams();
  const section = sectionProp || sectionUrl;
  const can = useCan();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [schema, setSchema] = useState(null);
  const [secrets, setSecrets] = useState({});
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [canWrite, setCanWrite] = useState(false);

  const meta = LABELS[section] || { title: section, fields: {} };

  useEffect(() => {
    let aborted = false;
    setLoading(true);
    setError(null);
    setSavedAt(null);
    api.get(`/api/settings/${section}`)
      .then((res) => {
        if (aborted) return;
        setSchema(res.schema);
        setSecrets(res.secrets || {});
        setValues(res.value || {});
        setCanWrite(Boolean(res.canWrite));
      })
      .catch((e) => {
        if (aborted) return;
        setError(e instanceof ApiError ? e.message : 'Не удалось загрузить настройки');
      })
      .finally(() => { if (!aborted) setLoading(false); });
    return () => { aborted = true; };
  }, [section]);

  if (!can(`settings.${section}`, 'read')) {
    return <div className="settings-card"><h2>Нет доступа</h2><p>У вас нет права на чтение этого раздела.</p></div>;
  }
  if (loading) return <div className="settings-card">Загрузка…</div>;
  if (error) return <div className="settings-card"><div style={{ color: 'var(--red-fg)' }}>Ошибка: {error}</div></div>;
  if (!schema) return null;

  const onChange = (name, val) => {
    setValues((v) => ({ ...v, [name]: val }));
    setSavedAt(null);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const res = await api.put(`/api/settings/${section}`, { value: values });
      setSchema(res.schema);
      setSecrets(res.secrets || {});
      setValues(res.value || {});
      setSavedAt(new Date());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="settings-card" onSubmit={onSubmit}>
      <div className="settings-card__head">
        <h2 className="settings-card__title">{meta.title}</h2>
        {meta.description && <p className="settings-card__sub">{meta.description}</p>}
      </div>

      <div className="settings-grid">
        {Object.entries(schema).map(([name, fmeta]) => {
          const label = meta.fields?.[name] || name;
          const id = `f-${section}-${name}`;
          const isSecret = fmeta.type === 'secret';
          const isInt = fmeta.type === 'int';
          const isFloat = fmeta.type === 'float';
          const isEnum = typeof fmeta.type === 'string' && fmeta.type.startsWith('enum:');
          const isMultiline = (meta.multiline || []).includes(name);
          const enumOpts = meta.enums?.[name];
          const value = values[name] ?? '';
          const placeholder = isSecret && secrets[name] ? '•••••• (оставьте пустым, чтобы не менять)' : undefined;

          return (
            <div key={name} className={'settings-field' + (isMultiline ? ' settings-field--wide' : '')}>
              <label className="settings-field__label" htmlFor={id}>{label}</label>
              {isEnum && enumOpts ? (
                <select
                  id={id}
                  className="settings-field__input"
                  value={value || ''}
                  onChange={(e) => onChange(name, e.target.value)}
                  disabled={!canWrite}
                >
                  <option value="">— не выбрано —</option>
                  {enumOpts.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
                </select>
              ) : isMultiline ? (
                <textarea
                  id={id}
                  className="settings-field__input"
                  rows={4}
                  value={value === null ? '' : value}
                  onChange={(e) => onChange(name, e.target.value)}
                  readOnly={!canWrite}
                  style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}
                />
              ) : (
                <input
                  id={id}
                  className="settings-field__input"
                  type={isSecret ? 'password' : (isInt || isFloat) ? 'number' : 'text'}
                  step={isFloat ? 'any' : undefined}
                  value={value === null ? '' : value}
                  placeholder={placeholder}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (isInt && v !== '')        onChange(name, Number(v));
                    else if (isFloat && v !== '') onChange(name, Number(v));
                    else                          onChange(name, v);
                  }}
                  readOnly={!canWrite}
                  autoComplete={isSecret ? 'new-password' : 'off'}
                />
              )}
              {isSecret && secrets[name] && canWrite && (
                <div className="settings-field__hint">Значение задано. Введите новое или оставьте пустым.</div>
              )}
              {meta.hints?.[name] && (
                <div className={'settings-field__hint' + (meta.warnings?.includes(name) ? ' settings-field__hint--warn' : '')}>
                  {meta.hints[name]}
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="settings-card__foot">
        {error && <div style={{ color: 'var(--red-fg)', fontSize: 12.5 }}>{error}</div>}
        {savedAt && !error && (
          <div style={{ color: 'var(--green-700)', fontSize: 12.5 }}>
            Сохранено в {savedAt.toLocaleTimeString('ru-RU')}
          </div>
        )}
        <div className="settings-card__spacer" />
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={!canWrite || saving}
          title={canWrite ? undefined : 'Нет права на запись'}
        >
          {saving ? 'Сохраняем…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}
